import { useEffect, useState, useCallback } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { api } from '../services/api';

function EsnSelect({ value, onChange, esns, placeholder = 'Selecionar ESN' }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
      className="text-xs border border-white/20 rounded px-2 py-1 bg-[#1a1a2e] text-white/80 min-w-[160px]"
    >
      <option value="">{placeholder}</option>
      {esns.map(e => (
        <option key={e.id} value={e.id}>{e.nome}</option>
      ))}
    </select>
  );
}

function RecordsTab({ records, assigns, onAssign, onBulkAssign, activeEsns, labelField, subLabelField }) {
  const [bulkEsn, setBulkEsn] = useState(null);

  function handleApplyBulk() {
    if (!bulkEsn) return;
    onBulkAssign(records.map(r => r.id), bulkEsn);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
        <span className="text-xs text-white/60 shrink-0">Transferir todos para:</span>
        <EsnSelect value={bulkEsn} onChange={setBulkEsn} esns={activeEsns} />
        <button
          onClick={handleApplyBulk}
          disabled={!bulkEsn}
          className="text-xs px-3 py-1 rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
        >
          Aplicar
        </button>
      </div>
      <div className="flex flex-col gap-1 max-h-[55vh] overflow-y-auto pr-1">
        {records.length === 0 && (
          <p className="text-xs text-white/40 text-center py-8">Nenhum registro nesta categoria</p>
        )}
        {records.map(rec => (
          <div
            key={rec.id}
            className="flex items-center gap-3 px-3 py-2 rounded hover:bg-white/5 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white/90 truncate">{rec[labelField] || rec.razao_social || rec.nome}</p>
              {rec[subLabelField] && (
                <p className="text-xs text-white/40">{rec[subLabelField]}</p>
              )}
            </div>
            <EsnSelect
              value={assigns[rec.id] || null}
              onChange={val => onAssign(rec.id, val)}
              esns={activeEsns}
              placeholder="Atribuir ESN"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PortfolioTransferDrawer({ onClose, onTransferred }) {
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);
  const [data, setData]             = useState(null);
  const [activeEsns, setActiveEsns] = useState([]);
  const [activeTab, setActiveTab]   = useState('leads');
  const [leadAssigns, setLeadAssigns]   = useState({});
  const [contaAssigns, setContaAssigns] = useState({});

  useEffect(() => {
    Promise.all([
      api.getOrphanRecords(true),
      api.getEsns(),
    ]).then(([orphans, esns]) => {
      setData(orphans);
      const mapped = (Array.isArray(esns) ? esns : []).map(e => ({
        ...e,
        id: e.legacy_esn_id ?? e.esn_id ?? e.id,
      }));
      setActiveEsns(mapped);
    }).catch(() => setError('Erro ao carregar dados. Tente novamente.')).finally(() => setLoading(false));
  }, []);

  const handleAssignLead  = useCallback((id, esnId) => setLeadAssigns(prev => ({ ...prev, [id]: esnId })), []);
  const handleAssignConta = useCallback((id, esnId) => setContaAssigns(prev => ({ ...prev, [id]: esnId })), []);
  const handleBulkLeads   = useCallback((ids, esnId) => setLeadAssigns(prev => ({ ...prev, ...Object.fromEntries(ids.map(id => [id, esnId])) })), []);
  const handleBulkContas  = useCallback((ids, esnId) => setContaAssigns(prev => ({ ...prev, ...Object.fromEntries(ids.map(id => [id, esnId])) })), []);

  const allLeadsAssigned  = data?.leads?.every(l => leadAssigns[l.id]) ?? true;
  const allContasAssigned = data?.contas?.every(c => contaAssigns[c.id]) ?? true;
  const canConfirm        = !submitting && allLeadsAssigned && allContasAssigned && (data?.total ?? 0) > 0;
  const missingCount      = (data?.leads?.filter(l => !leadAssigns[l.id]).length ?? 0)
                          + (data?.contas?.filter(c => !contaAssigns[c.id]).length ?? 0);

  const handleConfirm = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const leads  = Object.entries(leadAssigns).map(([id, esn_id]) => ({ id: Number(id), esn_id }));
      const contas = Object.entries(contaAssigns).map(([id, esn_id]) => ({ id: Number(id), esn_id }));
      const result = await api.transferPortfolio(leads, contas);
      onTransferred(result);
      onClose();
    } catch (err) {
      setError(err?.message || 'Falha na transferência. Nenhuma alteração foi salva.');
    } finally {
      setSubmitting(false);
    }
  }, [leadAssigns, contaAssigns, onTransferred, onClose]);

  const esnNames = data?.esns?.map(e => e.nome).join(', ') || '';

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-[70vw] max-w-3xl z-50 flex flex-col bg-[#12121e] border-l border-white/10 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-white">Transferência de Carteira</h2>
            {esnNames && <p className="text-xs text-white/50 mt-0.5">ESN inativo: {esnNames}</p>}
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col px-6 py-4 gap-4">
          {loading && (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="animate-spin text-white/40" size={28} />
            </div>
          )}
          {!loading && error && (
            <div className="flex items-center gap-2 p-3 rounded bg-red-500/10 text-red-400 text-sm">
              <AlertCircle size={16} />{error}
            </div>
          )}
          {!loading && data && data.total === 0 && (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-white/40 text-sm">Nenhum registro órfão encontrado.</p>
            </div>
          )}
          {!loading && data && data.total > 0 && (
            <>
              <div className="flex gap-1 border-b border-white/10 shrink-0">
                {[
                  { key: 'leads',  label: `Leads em andamento (${data.leads_count})` },
                  { key: 'contas', label: `Contas da base (${data.contas_count})` },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors -mb-px ${
                      activeTab === tab.key
                        ? 'border-indigo-500 text-indigo-400'
                        : 'border-transparent text-white/50 hover:text-white/80'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {activeTab === 'leads' ? (
                <RecordsTab
                  records={data.leads}
                  assigns={leadAssigns}
                  onAssign={handleAssignLead}
                  onBulkAssign={handleBulkLeads}
                  activeEsns={activeEsns}
                  labelField="razao_social"
                  subLabelField="stage"
                />
              ) : (
                <RecordsTab
                  records={data.contas}
                  assigns={contaAssigns}
                  onAssign={handleAssignConta}
                  onBulkAssign={handleBulkContas}
                  activeEsns={activeEsns}
                  labelField="nome"
                  subLabelField="cidade"
                />
              )}
            </>
          )}
        </div>

        {!loading && data && data.total > 0 && (
          <div className="shrink-0 px-6 py-4 border-t border-white/10 flex items-center justify-between gap-4">
            {error && <p className="text-xs text-red-400 flex-1">{error}</p>}
            {!error && missingCount === 0 && (
              <p className="text-xs text-emerald-400/70 flex-1">Tudo pronto para transferir</p>
            )}
            {missingCount > 0 && !error && (
              <p className="text-xs text-white/40 flex-1">
                {missingCount} registro{missingCount > 1 ? 's' : ''} sem ESN destino selecionado
              </p>
            )}
            <button
              onClick={handleConfirm}
              disabled={!canConfirm}
              title={missingCount > 0 ? `${missingCount} registro(s) sem ESN selecionado` : ''}
              className="px-5 py-2 rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors flex items-center gap-2"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Confirmar Transferência
            </button>
          </div>
        )}
      </div>
    </>
  );
}
